import React from 'react';
import { Box, Typography, Stack, Chip } from '@mui/material';
import type { StructuredRisk } from '../../utils/dashboard';
import { dashboardCopy } from '../../copy/dashboard';
import WhyAffordance from '../ai/WhyAffordance';

// ---------------------------------------------------------------------------
// RiskStrip — surfaces only at-risk and breach items. Absence is the good
// state; when there are no risks we show a single calm "on track" line.
// Colour comes from theme.palette.status so dark/light resolve correctly.
// ---------------------------------------------------------------------------

interface RiskStripProps {
  risks: StructuredRisk[];
  loading?: boolean;
  /** Optional narrative from the AI describing why the flagged risks matter.
   *  Shown behind a "Why?" affordance next to the header so the strip stays
   *  compact. */
  aiRationale?: string | null;
}

export function RiskStrip({ risks, loading = false, aiRationale }: RiskStripProps) {
  if (loading) {
    return (
      <Box sx={{ height: 48, borderRadius: 2, bgcolor: 'background.paper', opacity: 0.5 }} />
    );
  }

  if (risks.length === 0) {
    return (
      <Box
        sx={{
          px: 2.5,
          py: 1.5,
          borderRadius: 2,
          bgcolor: 'background.paper',
          border: 1,
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
        }}
      >
        <Box
          sx={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            bgcolor: 'status.onPlan',
          }}
        />
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {dashboardCopy.risksEmpty}
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 1 }}>
        <Typography
          variant="overline"
          sx={{ color: 'text.secondary', letterSpacing: '0.12em', fontWeight: 600, display: 'block' }}
        >
          {dashboardCopy.risksHeader}
        </Typography>
        <WhyAffordance
          rationale={aiRationale}
          size="compact"
          label="Why these risks"
        />
      </Stack>
      <Stack spacing={1}>
        {risks.map((risk, idx) => (
          <RiskRow key={`${risk.type}-${idx}`} risk={risk} />
        ))}
      </Stack>
    </Box>
  );
}

function RiskRow({ risk }: { risk: StructuredRisk }) {
  const statusColour = risk.severity === 'breach' ? 'status.breach' : 'status.atRisk';
  const badgeLabel = risk.severity === 'breach' ? 'Breach' : 'At risk';

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1.5,
        px: 2.5,
        py: 1.5,
        borderRadius: 2,
        bgcolor: 'background.paper',
        border: 1,
        borderColor: 'divider',
        // A thin coloured left edge marks severity without flooding the row
        // with colour — colour is reserved for meaning, not decoration.
        position: 'relative',
        '&::before': {
          content: '""',
          position: 'absolute',
          left: 0, top: 8, bottom: 8,
          width: 3,
          borderRadius: 2,
          bgcolor: statusColour,
        },
        pl: 3,
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {risk.label}
          </Typography>
          <Chip
            label={badgeLabel}
            size="small"
            sx={{
              height: 18,
              fontSize: 10,
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
              bgcolor: 'transparent',
              color: statusColour,
              border: 1,
              borderColor: statusColour,
            }}
          />
        </Stack>
        {risk.detail && (
          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.25 }}>
            {risk.detail}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

export default RiskStrip;
